import React, { useEffect, useCallback, useState } from "react";
import { ReactComponent as AlertIcon } from "assets/AlertIcon.svg";
import { Button, Modal, RadioButton } from "@pickit/pickit-components";
import "./AlertMasiveCancelation.scss";
import { AlertMasiveCancelationTyopes } from "./types";
import { useTranslation } from "react-i18next";
import { KEYS } from "component/modal/types";
import { CANCEL_TRANSATIONS_IDS } from "../../../utils/constants";

const AlertMasiveCancelation = ({
  isOpen,
  title,
  body,
  onClose,
  onCancel,
  errorTransactions = [],
  transactionsSelected,
  showNotification,
  testId = 'massive-cancelation',
}: AlertMasiveCancelationTyopes) => {
  const { t } = useTranslation();
  const [, setRadioActive] = useState(false);
  const [checkBoxSelected, setCheckBoxSelected] = useState<number>(0);

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (
        [KEYS.ESC, KEYS.ENTER, KEYS.NUM_ENTER].includes(event.code) &&
        isOpen
      ) {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    },
    [isOpen, onClose]
  );
  useEffect(() => {
    setRadioActive(false);
  }, []);
  useEffect(() => {
    if (isOpen) document.addEventListener("keydown", onKeyDown);
    else document.removeEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onKeyDown]);
  const handleCheckboxClick = (value: number) => {
    setCheckBoxSelected(value);
    setRadioActive(true);
  };

  const renderConfirmCancelation = useCallback(() => {
    return (
      <>
        <div className="cancelation-alert-body-container">
          <div className="cancelation-alert-body" data-testId={`${testId}-alert-body-container`}>{body}</div>
        </div>
        <div className="cancelation-alert-radio-container">
          <RadioButton
            name="state"
            value={CANCEL_TRANSATIONS_IDS.byAdmin}
            label={t("transactions:cancelByAdmin")}
            meta={{}}
            status="warning"
            checked={checkBoxSelected === CANCEL_TRANSATIONS_IDS.byAdmin}
            input={{
              name: "state",
              value: CANCEL_TRANSATIONS_IDS.byAdmin,
              onBlur: () => {},
              onFocus: () => {},
              onChange(event) {
                handleCheckboxClick(event);
              },
            }}
            testId={`${testId}-cancelled-by-admin`}
          />
          <RadioButton
            name="state"
            value={CANCEL_TRANSATIONS_IDS.bySeller}
            label={t("transactions:cancelBySeller")}
            meta={{}}
            checked={checkBoxSelected === CANCEL_TRANSATIONS_IDS.bySeller}
            status="warning"
            input={{
              name: "state",
              value: CANCEL_TRANSATIONS_IDS.bySeller,
              onBlur: () => {},
              onFocus: () => {},
              onChange(event) {
                handleCheckboxClick(event);
              },
            }}
            testId={`${testId}-cancelled-by-seller`}
          />
        </div>
        <div className="alert-masive-cancelation-button-container">
          <Button
            label={t("global:close")}
            status="warning"
            hierarchy="secondary"
            type="button"
            onClick={()=>onClose(false)}
            testId={`${testId}-option-close`}
          />
          <Button
            label={t("global:confirm")}
            status="warning"
            type="button"
            onClick={() => {
              onCancel({
                transactionsIds: transactionsSelected.map(
                  (trx) => trx.transaction?.id
                ),
                cancellationReasonId: checkBoxSelected,
              });
            }}
            disabled={checkBoxSelected === 0}
            testId={`${testId}-option-cancel`}
          />
        </div>
      </>
    );
  }, [body, checkBoxSelected, onCancel, onClose, t, transactionsSelected, testId]);
  const renderErrorCancelation = useCallback(() => {
    return (
      <>
        <div className="cancelation-alert-error-container">
          <div className="cancelation-alert-body-container">
            <div className="cancelation-alert-body">{body}</div>
          </div>
          <div className="cancelation-error-item-container">
            {errorTransactions &&
              errorTransactions.map((error, index) => (
                <p key={index} className="cancelation-alert-error-item">
                  {error}
                </p>
              ))}
          </div>
        </div>
        <div className="cancelation-error-button-container">
          <Button
            label={t("global:understood")}
            status="warning"
            type="button"
            onClick={()=>onClose()}
          />
        </div>
      </>
    );
  }, [body, errorTransactions, onClose, t]);

  const cancelationErrorAlert = useCallback(() => {
    onClose();
    showNotification({
      level: "error",
      title: t(
        `transactions${
          errorTransactions.length > 1
            ? ":errorMasiveCancelationTitle"
            : ":errorMasiveCancelationTitleSingular"
        }`
      ),
      body: t(
        `transactions${
          errorTransactions.length > 1
            ? ":errorMasiveCancelationBody"
            : ":errorMasiveCancelationBodySingular"
        }`
      ),
    });
  }, [errorTransactions.length, onClose, showNotification, t]);

  return (
    <Modal
      isOpen={isOpen}
      width="560px"
      height="auto"
      onClose={()=>onClose(false)}
      closeOnBlur={false}
      overAll
      testId={testId}
    >
      <div className="alert">
        <div className="alert-container">
          <div className="alert-icon-container" data-testId={`${testId}-icon-container"`}>
            <AlertIcon />
          </div>
          <div className="alert-title-container">
            <div className="cancelation-alert-title" data-testId={`${testId}-alert-title"`}>{title}</div>
          </div>
          {errorTransactions.length === 0 && renderConfirmCancelation()}
          {errorTransactions.length > 0 &&
            errorTransactions.length < transactionsSelected.length &&
            renderErrorCancelation()}
          {errorTransactions.length === transactionsSelected.length &&
            errorTransactions.length > 0 &&
            cancelationErrorAlert()}
        </div>
      </div>
    </Modal>
  );
};

export default AlertMasiveCancelation;
